import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SafeImage from "@/components/SafeImage";
import { Plus, Copy, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";
import { humanStatus, humanInventory } from "@/lib/labels";

export default function ProductsList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const filtered = items.filter((p) => !q || p.title.toLowerCase().includes(q.toLowerCase()));
  const del = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load();
  };
  const dup = async (id) => { await api.post(`/admin/products/${id}/duplicate`); toast.success("Duplicated"); load(); };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="font-display text-2xl sm:text-3xl text-gold flex-1">Products</h1>
        <input data-testid="admin-products-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="input-vintage max-w-full sm:max-w-xs" />
        <Link to="/admin/products/new" data-testid="admin-product-add" className="btn-gold text-xs whitespace-nowrap"><Plus size={14} className="mr-2" /> Add Product</Link>
      </div>

      {/* Mobile: cards */}
      <div className="grid grid-cols-1 sm:hidden gap-3" data-testid="admin-products-mobile">
        {filtered.map((p) => (
          <div key={p.id} className="curio-card p-3 flex gap-3" data-testid={`admin-product-card-${p.id}`}>
            <div className="w-16 h-20 flex-shrink-0 bg-[color:var(--forest-2)]">
              <SafeImage src={p.primary_image} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-cream text-sm font-serif leading-tight truncate">{p.title}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-parchment/70 mt-1">
                <span className="text-gold font-caption">${p.price.toFixed(2)}</span>
                <span>{humanStatus(p.product_status)}</span>
                <span>{humanInventory(p.inventory_type)} · {p.inventory_quantity}</span>
              </div>
              {p.collection && <div className="text-[10px] text-brass/80 font-caption mt-1">{p.collection.replace(/-/g, ' ')}</div>}
              <div className="flex gap-1 mt-2">
                <Link to={`/admin/products/${p.id}`} className="btn-outline text-[10px] py-1 px-2 flex-1 text-center">Edit</Link>
                <button onClick={() => dup(p.id)} className="btn-outline text-[10px] py-1 px-2"><Copy size={12} /></button>
                <button onClick={() => del(p.id)} className="btn-outline text-[10px] py-1 px-2 hover:!text-oxblood"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tablet/Desktop: table */}
      <div className="hidden sm:block curio-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-brass font-caption text-[10px]">
            <tr className="border-b border-brass-soft">
              <th className="text-left p-3">Image</th><th className="text-left p-3">Title</th>
              <th className="text-left p-3">Status</th><th className="text-left p-3">Price</th>
              <th className="text-left p-3">Type</th><th className="text-left p-3">Qty</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-brass-soft/40" data-testid={`admin-product-row-${p.id}`}>
                <td className="p-3"><div className="w-12 h-14 bg-[color:var(--forest-2)]"><SafeImage src={p.primary_image} alt="" className="w-full h-full object-cover" /></div></td>
                <td className="p-3 text-cream">{p.title}{p.featured && <span className="ml-2 text-[9px] text-gold font-caption">FEATURED</span>}{p.is_new_arrival && <span className="ml-2 text-[9px] text-brass font-caption">NEW</span>}{p.is_sale && <span className="ml-2 text-[9px] text-oxblood font-caption">SALE</span>}</td>
                <td className="p-3 text-parchment/70">{humanStatus(p.product_status)}</td>
                <td className="p-3 text-gold">${p.price.toFixed(2)}</td>
                <td className="p-3 text-parchment/70">{humanInventory(p.inventory_type)}</td>
                <td className="p-3 text-parchment/70">{p.inventory_quantity}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Link to={`/admin/products/${p.id}`} data-testid={`edit-${p.id}`} className="inline-block p-1.5 text-brass hover:text-gold"><Edit size={14} /></Link>
                  <button data-testid={`dup-${p.id}`} onClick={() => dup(p.id)} className="p-1.5 text-brass hover:text-gold"><Copy size={14} /></button>
                  <button data-testid={`del-${p.id}`} onClick={() => del(p.id)} className="p-1.5 text-brass hover:text-oxblood"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
